/**
 * React Query Hook: Get Active Branch
 *
 * Fetches the current user's active branch for a specific metadata item.
 * Returns null if no active branch exists.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { ACTIVE_BRANCH_QUERY } from '@/services/metadata_v1/queries';
import type {
  ActiveBranchResponse,
  ActiveBranchVariables,
  MetadataBranch,
} from '@/services/metadata_v1/types';

/** Use outside React Query (e.g. chat Compare baseline vs main line). */
export async function fetchActiveBranch(
  variables: ActiveBranchVariables
): Promise<MetadataBranch | null> {
  const response = await graphqlRequest<ActiveBranchResponse>(
    ACTIVE_BRANCH_QUERY,
    variables
  );
  return response.activeBranch;
}

/**
 * Hook to get the current user's active branch for a metadata item.
 *
 * Use this when loading a file in the editor to check if the user has an
 * in-progress branch. If so, show branch content; otherwise show main content.
 *
 * @param metadataItemId - The ID of the metadata item to check
 * @param options - Query options
 * @returns React Query result — data is null when no active branch exists
 */
export function useActiveBranch(
  metadataItemId: string | null | undefined,
  options?: { enabled?: boolean }
): UseQueryResult<MetadataBranch | null, Error> {
  const { enabled = true } = options || {};

  return useQuery({
    queryKey: ['activeBranch', metadataItemId],
    queryFn: () =>
      fetchActiveBranch({ metadataItemId: metadataItemId! }),
    enabled: enabled && !!metadataItemId,
    staleTime: 0, // always fresh — branch state can change from other actions
    refetchOnWindowFocus: false,
  });
}
